import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6e85a7b3-7844-4782-8600-31defd3a9a43/artifacts/5fhyey8q_MV%20trasporti%20logo.png";

export default function Navbar() {
  const { t } = useTranslation();
  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/servizi", label: t("nav.services") },
    { path: "/chi-siamo", label: t("nav.about") },
    { path: "/blog", label: t("nav.blog") },
    { path: "/contatti", label: t("nav.contact") },
    { path: "/tracking", label: t("nav.client_area") },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-shadow duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-zinc-200"
          : "bg-white border-zinc-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="MV Trasporti"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`text-sm font-bold tracking-widest uppercase transition-colors duration-200 ${
                  location.pathname === link.path
                    ? "text-[#FF5200]"
                    : "text-[#09090B] hover:text-[#FF5200]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher className="hidden md:inline-flex" />
            <Link
              to="/prenota-ritiro"
              data-testid="nav-cta-prenota-ritiro"
              className="hidden md:inline-flex items-center gap-2 border-2 border-[#FF5200] text-[#FF5200] px-5 py-2 text-sm font-bold tracking-wide uppercase rounded-sm hover:bg-[#FF5200] hover:text-white transition-colors duration-200"
            >
              {t("nav.book_pickup")}
            </Link>
            <Link
              to="/preventivo"
              data-testid="nav-cta-preventivo"
              className="hidden md:inline-flex items-center gap-2 bg-[#FF5200] text-white px-5 py-2 text-sm font-bold tracking-wide uppercase rounded-sm hover:bg-[#e64a00] transition-colors duration-200"
            >
              {t("nav.quote")}
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-[#09090B]"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden bg-white border-t border-zinc-200"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                className={`block text-sm font-bold tracking-widest uppercase py-2 ${
                  location.pathname === link.path
                    ? "text-[#FF5200]"
                    : "text-[#09090B]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/prenota-ritiro"
              data-testid="mobile-cta-prenota-ritiro"
              className="block border-2 border-[#FF5200] text-[#FF5200] px-6 py-3 text-sm font-bold tracking-wide uppercase text-center rounded-sm"
            >
              {t("nav.book_pickup")}
            </Link>
            <Link
              to="/preventivo"
              data-testid="mobile-cta-preventivo"
              className="block bg-[#FF5200] text-white px-6 py-3 text-sm font-bold tracking-wide uppercase text-center rounded-sm"
            >
              {t("nav.quote")}
            </Link>
            <div className="pt-2 flex justify-center"><LanguageSwitcher /></div>
          </div>
        </div>
      )}
    </nav>
  );
}
